import { LegalPageViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useEffect, useMemo } from "react";
import { client, SITE } from "../client.js";

export interface PrivacyPageProps {
  className?: string;
  viewModel?: LegalPageViewModel;
}

/**
 * Data-driven Privacy Policy page (#902). Renders loading, error, empty, and ready states
 * from `site_content.legalPages.privacy` via `LegalPageViewModel`.
 */
export function PrivacyPage({ className, viewModel }: PrivacyPageProps) {
  const vm = useMemo(
    () => viewModel ?? new LegalPageViewModel(client, SITE, "privacy"),
    [viewModel],
  );
  const state = useViewModel(vm);

  useEffect(() => {
    if (!viewModel) {
      void vm.load();
    }
  }, [vm, viewModel]);

  if (state.loading) {
    return (
      <article className={className} data-lumora-view="privacy" data-state="loading">
        <p className="site-utility-kicker">Legal</p>
        <h1>Privacy policy</h1>
        <p>Loading privacy policy…</p>
      </article>
    );
  }

  if (state.error) {
    return (
      <article className={className} data-lumora-view="privacy" data-state="error">
        <p className="site-utility-kicker">Legal</p>
        <h1>Privacy policy</h1>
        <p data-lumora-error className="portal-error">{state.error}</p>
        <button type="button" className="portal-btn" onClick={() => void vm.load()}>
          Retry
        </button>
      </article>
    );
  }

  if (!state.document || state.document.isEmpty()) {
    return (
      <article className={className} data-lumora-view="privacy" data-state="empty">
        <p className="site-utility-kicker">Legal</p>
        <h1>Privacy policy</h1>
        <p className="portal-notice" data-lumora-empty>
          Privacy policy is being updated. Contact Lumora with any data or privacy inquiries.
        </p>
      </article>
    );
  }

  const doc = state.document;

  return (
    <article className={className} data-lumora-view="privacy" data-state="ready">
      <p className="site-utility-kicker">{doc.kicker}</p>
      <h1>{doc.title}</h1>
      {doc.lead ? (
        <p className="portal-notice site-utility-lead" data-lumora-lead>
          {doc.lead}
        </p>
      ) : null}
      {doc.sections.map((section) => (
        <section key={section.heading} className="portal-section">
          <h2>{section.heading}</h2>
          {section.lead ? <p data-lumora-lead>{section.lead}</p> : null}
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.subsections?.map((block) => (
            <div key={block.heading}>
              <h3>{block.heading}</h3>
              {block.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          ))}
        </section>
      ))}
    </article>
  );
}
